import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Form } from 'components/ui/atoms';
import MonarchResolver from 'components/ui/organisms/monarch/MonarchResolver';
import { GeoPricingResult } from 'components/ui/organisms/monarch/rules/geoPricing/types';
import { SHORT_FORM_CONFIG_BY_COUNTRY, VARIANT_CONFIGS } from './constants';
import { CountrySelect } from './country-select';
import { useCountryOptions, useStateOptions } from './hooks';
import { CountrySelectSkeleton } from './skeleton';
import { AddressFormProps, AddressSchema } from './types';
import './address-form.scss';

/**
 * AddressForm component
 *
 * handles address field rendering and coordination.
 */

export const AddressForm = ({
  isDisabled,
  useShortAddressForm = false,
  showCityField = false,
  variant = 'checkout',
  className,
  products,
  isMinimalLayout = false,
  onBillingCountryChange,
}: AddressFormProps) => {
  const config = VARIANT_CONFIGS[variant];
  const fieldStyleClasses = isMinimalLayout ? 'col-lg-6 mb-2' : 'col-lg-6';
  const inputStyleClasses = isMinimalLayout
    ? `${fieldStyleClasses} address-form__minimal-input`
    : fieldStyleClasses;
  const countryOptions = useCountryOptions();
  const { control, setValue, setError } = useFormContext<AddressSchema>();

  const selectedCountry = useWatch({
    control,
    name: 'country',
    defaultValue: '',
  });

  const stateOptions = useStateOptions(selectedCountry);
  const hasStateOptions = stateOptions.length > 1;

  // Clear selected state when selected country changes
  useEffect(() => {
    setValue('stateCode', hasStateOptions ? '' : 'not-required');
    setValue('state', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  // Bubble the live billing country up so the parent checkout component can
  // country-gate UPI eligibility (usePayInContext) without lifting this form
  // out of Elements.
  useEffect(() => {
    onBillingCountryChange?.(selectedCountry);
  }, [selectedCountry, onBillingCountryChange]);

  useEffect(() => {
    if (!showCityField) {
      return;
    }

    setError(
      'city',
      { type: 'required', message: 'payment.form.validation.required' },
      { shouldFocus: true },
    );
  }, [showCityField, setError]);

  const renderStateField = () =>
    hasStateOptions ? (
      <Form.Select
        name="stateCode"
        label={
          <FormattedMessage
            id="payment.form.address.state.required"
            defaultMessage="State/Province (required)"
            description="Label for State Select"
          />
        }
        options={stateOptions}
        autocomplete="address-level1"
        styleClasses={inputStyleClasses}
        disabled={isDisabled}
      />
    ) : (
      <Form.Input
        name="state"
        label={
          <FormattedMessage
            id="payment.form.address.state"
            defaultMessage="State/Province"
            description="Label for Form Input"
          />
        }
        autocomplete="address-level1"
        styleClasses={inputStyleClasses}
        maxLength={20}
        disabled={isDisabled}
      />
    );

  const renderPostalField = () => (
    <Form.Input
      name="postalCode"
      label={
        <FormattedMessage
          id="payment.form.address.postal"
          defaultMessage="Zip/Postal code"
          description="Label for Form Input"
        />
      }
      autocomplete="postal-code"
      styleClasses={inputStyleClasses}
      maxLength={9}
      disabled={isDisabled}
    />
  );

  const buildStateAndPostalCodeFields = () => {
    if (useShortAddressForm) {
      const fieldConfig = SHORT_FORM_CONFIG_BY_COUNTRY[selectedCountry] ?? {
        showState: false,
        showPostal: false,
      };
      return (
        <>
          {fieldConfig.showState && renderStateField()}
          {fieldConfig.showPostal && renderPostalField()}
        </>
      );
    }

    // Full form: always show both
    return (
      <>
        {renderStateField()}
        {renderPostalField()}
      </>
    );
  };

  const fieldsetClass = className ?? config.fieldsetClassName;
  const headingClassName = isMinimalLayout
    ? 'address-form__minimal-heading'
    : config.headingConfig?.headingClassName;

  return (
    <fieldset className={fieldsetClass}>
      {config.showHeading && config.headingConfig && (
        <legend className={config.headingConfig.legendClassName}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <h2 className={headingClassName}>
              <FormattedMessage
                id={config.headingConfig.messageId}
                defaultMessage={config.headingConfig.defaultMessage}
                description="Heading for Address Form"
              />
            </h2>
          </div>
        </legend>
      )}

      {/* Name fields - always visible */}
      <Form.Row>
        <Form.Input
          name="firstName"
          label={
            <FormattedMessage
              id="payment.form.address.first.name"
              defaultMessage="First name (required)"
              description="Label for Form Input"
            />
          }
          autocomplete="given-name"
          styleClasses={inputStyleClasses}
          disabled={isDisabled}
        />
        <Form.Input
          name="lastName"
          label={
            <FormattedMessage
              id="payment.form.address.last.name"
              defaultMessage="Last name (required)"
              description="Label for Form Input"
            />
          }
          autocomplete="family-name"
          styleClasses={inputStyleClasses}
          disabled={isDisabled}
        />
      </Form.Row>

      {/* Address fields - conditional on short form */}
      {!useShortAddressForm && (
        <Form.Row>
          <Form.Input
            name="address"
            label={
              <FormattedMessage
                id="payment.form.address.address"
                defaultMessage="Address (required)"
                description="Label for Form Input"
              />
            }
            autocomplete="street-address"
            styleClasses={inputStyleClasses}
            maxLength={60}
            disabled={isDisabled}
          />
          <Form.Input
            name="unit"
            label={
              <FormattedMessage
                id="payment.form.address.suite"
                defaultMessage="Suite/Apartment number"
                description="Label for Form Input"
              />
            }
            autocomplete="address-line2"
            styleClasses={inputStyleClasses}
            maxLength={29}
            disabled={isDisabled}
          />
        </Form.Row>
      )}

      {/* City and Country row */}
      <Form.Row>
        {!useShortAddressForm && (
          <Form.Input
            name="city"
            label={
              <FormattedMessage
                id="payment.form.address.city"
                defaultMessage="City (required)"
                description="Label for Form Input"
              />
            }
            autocomplete="address-level2"
            styleClasses={inputStyleClasses}
            disabled={isDisabled}
          />
        )}

        <MonarchResolver<GeoPricingResult>
          experimentKey="useGeoPricing"
          variants={{
            control: (
              <Form.Select
                name="country"
                label={
                  <FormattedMessage
                    id="payment.form.address.country"
                    defaultMessage="Country (required)"
                    description="Label for Country Select"
                  />
                }
                options={countryOptions}
                autocomplete="country"
                styleClasses={inputStyleClasses}
                disabled={isDisabled}
              />
            ),
            test: (
              <CountrySelect
                options={countryOptions}
                disabled={isDisabled}
                products={products}
              />
            ),
          }}
          fallbackVariant="control"
          Loader={<CountrySelectSkeleton />}
        />

        {buildStateAndPostalCodeFields()}

        {useShortAddressForm && showCityField && (
          <Form.Input
            name="city"
            label={
              <FormattedMessage
                id="payment.form.address.city"
                defaultMessage="City (required)"
                description="Label for Form Input"
              />
            }
            autocomplete="address-level2"
            styleClasses={inputStyleClasses}
            disabled={isDisabled}
          />
        )}
      </Form.Row>
    </fieldset>
  );
};
